// src/components/Magazine/MagazineShopCreate.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditableFormField } from '@/components/admin/EditableFormField';
import { ImageUpload } from '@/components/admin/ImageUpload';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { postAdminNeighborhood } from '@/API/apiService';
import { DatePickerField } from '@/components/admin/DatePickerField';
import {
  useNeighborhoodList,
  usePropertiesList,
  Property,
  Area,
  Tag,
} from '@/components/admin/DropDowns';
import { MultiDropdownField } from '@/components/admin/Common/MultiDropdownField';

interface FormState {
  list_title: string;
  list_title_en: string;
  date_time: string;
  information: string;
  information_en: string;
  body: string;
  body_en: string;
  property1: string;
  property2: string;
  property3: string;
  areaTag1: string;
  areaTag2: string;
  areaTag3: string;
  categoryTag1: string;
  categoryTag2: string;
  categoryTag3: string;
  categoryTag4: string;
  categoryTag5: string;
  categoryTag6: string;
  title: string; // 概要（日本語）
  title_en: string; // 概要（英語）
  title_text: string; // 説明（日本語）
  title_text_en: string; // 説明（英語）
}

const LOCAL_STORAGE_KEY = 'magazineShopForm';

export const MagazineShopCreate: React.FC = () => {
  const navigate = useNavigate();

  const [modalError, setModalError] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormState>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Formデータのパースに失敗:', error);
      }
    }
    return {
      list_title: '',
      list_title_en: '',
      date_time: '',
      information: '',
      information_en: '',
      body: '',
      body_en: '',
      property1: '',
      property2: '',
      property3: '',
      areaTag1: '',
      areaTag2: '',
      areaTag3: '',
      categoryTag1: '',
      categoryTag2: '',
      categoryTag3: '',
      categoryTag4: '',
      categoryTag5: '',
      categoryTag6: '',
      title: '',
      title_en: '',
      title_text: '',
      title_text_en: '',
    };
  });

  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [otherImageFiles, setOtherImageFiles] = useState<File[]>([]);
  const [headerImagePreview, setHeaderImagePreview] = useState<string>('');
  const [otherImagesPreview, setOtherImagesPreview] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // useNeighborhoodList フックから areas と tags を取得
  const {
    areas,
    tags,
    loading: neighborhoodLoading,
    error: neighborhoodError,
  } = useNeighborhoodList();
  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
  } = usePropertiesList();

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleHeaderImageChange = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setHeaderImageFile(file);
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };

  const handleOtherImagesChange = (files: File[]) => {
    setOtherImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setOtherImagesPreview(previews);
  };

  const handleRemoveHeaderImage = () => {
    setHeaderImageFile(null);
    setHeaderImagePreview('');
  };

  const handleRemoveOtherImage = (index: number) => {
    setOtherImageFiles((prev) => prev.filter((_, i) => i !== index));
    setOtherImagesPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAll = async () => {
    const requiredFields: { key: keyof FormState; label: string }[] = [
      { key: 'list_title', label: '施設名 (日本語表記)' },
      { key: 'list_title_en', label: '施設名 (英語表記)' },
      { key: 'date_time', label: '追加日' },
      { key: 'information', label: '店舗情報 (日本語)' },
      { key: 'information_en', label: '店舗情報 (英語)' },
      { key: 'body', label: '本文 (日本語)' },
      { key: 'body_en', label: '本文 (英語)' },
      { key: 'title', label: '概要 (日本語)' },
      { key: 'title_en', label: '概要 (英語)' },
      { key: 'title_text', label: '説明 (日本語)' },
      { key: 'title_text_en', label: '説明 (英語)' },
    ];
    const missing = requiredFields.filter(
      (field) => !formData[field.key] || formData[field.key].trim() === ''
    );
    if (missing.length > 0) {
      setModalError(true);
      return;
    }

    try {
      setLoading(true);
      const fd = new FormData();

      // テキスト系のフィールドを送信用に追加
      const textFields = [
        'list_title',
        'list_title_en',
        'date_time',
        'information',
        'information_en',
        'body',
        'body_en',
        'title',
        'title_en',
        'title_text',
        'title_text_en',
      ];
      textFields.forEach((field) => {
        const value = formData[field as keyof FormState];
        if (field === 'date_time' && value) {
          const parts = value.toString().split('.');
          if (parts.length === 3) {
            const [year, month, day] = parts;
            fd.append(
              field,
              `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            );
          } else {
            fd.append(field, value.toString());
          }
        } else {
          fd.append(field, value.toString());
        }
      });

      // ドロップダウンの各値を送信時はID（数値）としてまとめる

      // 紐付け物件（properties）は property1～3 をまとめる
      const propertiesArray = [
        formData.property1,
        formData.property2,
        formData.property3,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      propertiesArray.forEach((propId) => {
        fd.append('properties[]', propId.toString());
      });

      // エリアタグ（areas）は areaTag1～3 をまとめる
      const areasArray = [
        formData.areaTag1,
        formData.areaTag2,
        formData.areaTag3,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      areasArray.forEach((areaId) => {
        fd.append('areas[]', areaId.toString());
      });

      // カテゴリタグ（tags）は categoryTag1～6 をまとめる
      const tagsArray = [
        formData.categoryTag1,
        formData.categoryTag2,
        formData.categoryTag3,
        formData.categoryTag4,
        formData.categoryTag5,
        formData.categoryTag6,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      tagsArray.forEach((tagId) => {
        fd.append('tags[]', tagId.toString());
      });

      // 画像関連
      // ヘッダー画像はキーを header_image に変更（バックエンド仕様に合わせる）
      if (headerImageFile) {
        fd.append('header_image', headerImageFile);
      }
      otherImageFiles.forEach((file) => {
        fd.append('images[]', file);
      });

      const response = await postAdminNeighborhood(fd);
      console.log('新規作成成功:', response.data);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      navigate('/admin/magazine');
    } catch (error) {
      console.error('新規作成に失敗しました:', error);
      alert('作成失敗');
    } finally {
      setLoading(false);
    }
  };

  const requiredLabel = (label: string) => (
    <span>
      {label}
      <span style={{ color: 'red' }}>*</span>
    </span>
  );

  const isLoading = loading || neighborhoodLoading || propertiesLoading;
  const hasError = neighborhoodError || propertiesError;

  return (
    <AdminLayout>
      {isLoading ? (
        <div>Loading...</div>
      ) : hasError ? (
        <div>オプションの読み込みエラー</div>
      ) : (
        <div className="p-6 bg-yellow">
          {/* 施設名（日本語 + 英語） */}
          <EditableFormField
            label={requiredLabel('施設名')}
            field="list_title"
            subLabel1="(日本語表記)"
            defaultValue1={formData.list_title}
            subLabel2="(英語表記)"
            defaultValue2={formData.list_title_en}
            height={33}
            onValueChange={(values) => {
              handleInputChange('list_title', values.value1 || '');
              handleInputChange('list_title_en', values.value2 || '');
            }}
          />

          {/* 追加日 */}
          <DatePickerField
            label={requiredLabel('追加日')}
            value={formData.date_time}
            onChange={(newValue) => handleInputChange('date_time', newValue)}
            height={33}
          />

          {/* 店舗情報（日本語 + 英語） */}
          <EditableFormField
            label={requiredLabel('店舗情報')}
            field="information"
            subLabel1="(日本語表記)"
            defaultValue1={formData.information}
            subLabel2="(英語表記)"
            defaultValue2={formData.information_en}
            height={100}
            onValueChange={(values) => {
              handleInputChange('information', values.value1 || '');
              handleInputChange('information_en', values.value2 || '');
            }}
          />

          {/* 本文（日本語 + 英語） */}
          <EditableFormField
            label={requiredLabel('本文')}
            field="body"
            subLabel1="(日本語表記)"
            defaultValue1={formData.body}
            subLabel2="(英語表記)"
            defaultValue2={formData.body_en}
            height={150}
            onValueChange={(values) => {
              handleInputChange('body', values.value1 || '');
              handleInputChange('body_en', values.value2 || '');
            }}
          />

          {/* 紐付け物件 1〜3 → ドロップダウン */}
          <MultiDropdownField
            label="紐付け物件"
            count={3}
            values={[
              formData.property1,
              formData.property2,
              formData.property3,
            ]}
            options={properties.map((prop: Property) => ({
              id: prop.property_id,
              name: prop.property_name,
            }))}
            height={33}
            onChange={(newValues) => {
              handleInputChange('property1', newValues[0] || '');
              handleInputChange('property2', newValues[1] || '');
              handleInputChange('property3', newValues[2] || '');
            }}
          />

          {/* エリアタグ 1〜3 → ドロップダウン */}
          <MultiDropdownField
            label="エリアタグ"
            count={3}
            values={[formData.areaTag1, formData.areaTag2, formData.areaTag3]}
            options={areas.map((area: Area) => ({
              id: area.id,
              name: area.name,
            }))}
            height={33}
            onChange={(newValues) => {
              handleInputChange('areaTag1', newValues[0] || '');
              handleInputChange('areaTag2', newValues[1] || '');
              handleInputChange('areaTag3', newValues[2] || '');
            }}
          />

          {/* カテゴリタグ 1〜6 → ドロップダウン */}
          <MultiDropdownField
            label={requiredLabel('カテゴリタグ')}
            count={6}
            values={[
              formData.categoryTag1,
              formData.categoryTag2,
              formData.categoryTag3,
              formData.categoryTag4,
              formData.categoryTag5,
              formData.categoryTag6,
            ]}
            options={tags.map((tag: Tag) => ({
              id: tag.id,
              name: tag.name,
            }))}
            height={33}
            onChange={(newValues) => {
              handleInputChange('categoryTag1', newValues[0] || '');
              handleInputChange('categoryTag2', newValues[1] || '');
              handleInputChange('categoryTag3', newValues[2] || '');
              handleInputChange('categoryTag4', newValues[3] || '');
              handleInputChange('categoryTag5', newValues[4] || '');
              handleInputChange('categoryTag6', newValues[5] || '');
            }}
          />

          {/* 概要（日本語 + 英語） */}
          <EditableFormField
            label={requiredLabel('概要')}
            field="title"
            subLabel1="(日本語表記)"
            defaultValue1={formData.title}
            subLabel2="(英語表記)"
            defaultValue2={formData.title_en}
            height={150}
            onValueChange={(values) => {
              handleInputChange('title', values.value1 || '');
              handleInputChange('title_en', values.value2 || '');
            }}
          />

          {/* 説明（日本語 + 英語） */}
          <EditableFormField
            label={requiredLabel('説明')}
            field="title_text"
            subLabel1="(日本語表記)"
            defaultValue1={formData.title_text}
            subLabel2="(英語表記)"
            defaultValue2={formData.title_text_en}
            height={300}
            onValueChange={(values) => {
              handleInputChange('title_text', values.value1 || '');
              handleInputChange('title_text_en', values.value2 || '');
            }}
          />

          <ImageUpload
            label="トップ画像"
            images={
              headerImagePreview ? [{ id: 0, url: headerImagePreview }] : []
            }
            onAddImages={handleHeaderImageChange}
            onRemoveImage={handleRemoveHeaderImage}
          />

          <ImageUpload
            label={requiredLabel('写真登録')}
            images={otherImagesPreview.map((url, idx) => ({ id: idx, url }))}
            onAddImages={handleOtherImagesChange}
            onRemoveImage={handleRemoveOtherImage}
          />

          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={handleSaveAll}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              新規作成を実行
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default MagazineShopCreate;
